import { PageHeader } from "@/components/layout/header-provider";
import { api as trpc, trpcClient } from "@/lib/trpc";
import {
  Badge,
  Button,
  Card,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@repo/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
  ArrowLeft,
  Ban,
  ChevronDown,
  ExternalLink,
  Play,
  RefreshCw,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/jobs")({
  component: JobsPage,
});

const DATADOG_DASHBOARD_URL = "https://us5.datadoghq.com/dashboard/dsn-hub-8y6";

function datadogJobUrl(jobId: string): string {
  return `${DATADOG_DASHBOARD_URL}?tpl_var_job_id%5B0%5D=${jobId}&live=true`;
}

function datadogRunUrl(jobId: string, runId: string): string {
  return `${DATADOG_DASHBOARD_URL}?tpl_var_job_id%5B0%5D=${jobId}&tpl_var_run_id%5B0%5D=${runId}&live=true`;
}

// Single registry for all job types.
const JOB_TYPE_REGISTRY: Record<
  string,
  { label: string; description: string }
> = {
  paypal_backfill: {
    label: "PayPal Backfill",
    description: "Sync all PayPal payment emails to the legacy system",
  },
  mock_test: {
    label: "Mock Test",
    description: "Simulate 200 entities in 10 batches with ~20% failure rate",
  },
};

function jobTypeLabel(type: string) {
  return JOB_TYPE_REGISTRY[type]?.label ?? type.replace(/_/g, " ");
}

function statusBadgeVariant(status: string) {
  switch (status) {
    case "running":
      return "default" as const;
    case "completed":
    case "succeeded":
      return "secondary" as const;
    case "failed":
      return "destructive" as const;
    case "cancelled":
    default:
      return "outline" as const;
  }
}

function formatDate(date: string | Date) {
  return new Date(date).toLocaleString();
}

function JobsPage() {
  const [page, setPage] = useState(1);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);

  if (selectedJobId) {
    return (
      <JobDetailView
        jobId={selectedJobId}
        onBack={() => setSelectedJobId(null)}
      />
    );
  }

  return (
    <JobsListView
      page={page}
      setPage={setPage}
      onSelectJob={setSelectedJobId}
    />
  );
}

function JobsListView({
  page,
  setPage,
  onSelectJob,
}: {
  page: number;
  setPage: (p: number) => void;
  onSelectJob: (id: string) => void;
}) {
  const queryClient = useQueryClient();

  const { data: envData } = useQuery(
    trpc.admin.getLegacyEnvironment.queryOptions(),
  );

  const { data, isLoading, isError, error, refetch } = useQuery(
    trpc.admin.getJobs.queryOptions(
      { page, limit: 20 },
      {
        placeholderData: (prev) => prev,
        refetchInterval: (query) => {
          const jobs = query.state.data?.data;
          if (
            jobs?.some((j) => j.status === "running" || j.status === "pending")
          ) {
            return 3000;
          }
          return false;
        },
      },
    ),
  );

  const { mutate: triggerJob, isPending: isTriggering } = useMutation({
    mutationFn: (type: string) =>
      trpcClient.admin.triggerJob.mutate({
        type: type as "paypal_backfill" | "mock_test",
      }),
    onSuccess: (result) => {
      if (result.jobId) {
        toast.success(result.message);
        queryClient.invalidateQueries({
          queryKey: trpc.admin.getJobs.queryKey(),
        });
      } else {
        toast.info(result.message);
      }
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });

  return (
    <div className="flex flex-col gap-6 p-6 max-md:p-0 h-full">
      <PageHeader>
        <span className="font-semibold">Background Jobs</span>
      </PageHeader>

      <Card className="flex-1 flex flex-col overflow-hidden max-md:border-0 max-md:shadow-none max-md:bg-transparent max-md:rounded-none">
        {envData && (
          <div className="p-3 border-b bg-muted/30 flex flex-wrap gap-x-6 gap-y-1 text-xs font-mono text-muted-foreground">
            <span>
              <span className="font-sans font-medium text-foreground">ENV</span>{" "}
              {envData.environment}
            </span>
            <span>
              <span className="font-sans font-medium text-foreground">
                Aspirex
              </span>{" "}
              {envData.aspirexGraphqlUrl ?? "—"}
            </span>
            <span>
              <span className="font-sans font-medium text-foreground">
                Legacy Services
              </span>{" "}
              {envData.legacyServicesUrl ?? "—"}
            </span>
            <span>
              <span className="font-sans font-medium text-foreground">STA</span>{" "}
              {envData.staGraphqlUrl ?? "—"}
            </span>
            <span>
              <span className="font-sans font-medium text-foreground">PFA</span>{" "}
              {envData.pfaGraphqlUrl ?? "—"}
            </span>
          </div>
        )}

        <div className="p-4 border-b flex items-center justify-between bg-card text-card-foreground">
          <p className="text-sm text-muted-foreground">
            Manage and monitor background jobs
          </p>
          <div className="flex items-center gap-2">
            <a
              href={DATADOG_DASHBOARD_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Dashboard
            </a>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" disabled={isTriggering}>
                  <Play className="mr-2 h-4 w-4" />
                  {isTriggering ? "Triggering..." : "Trigger Job"}
                  <ChevronDown className="ml-2 h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {Object.entries(JOB_TYPE_REGISTRY).map(([type, meta]) => (
                  <DropdownMenuItem
                    key={type}
                    disabled={isTriggering}
                    onClick={() => {
                      if (confirm(`${meta.description}. Continue?`)) {
                        triggerJob(type);
                      }
                    }}
                  >
                    {meta.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          {isLoading ? (
            <div className="flex h-full items-center justify-center p-8 text-muted-foreground">
              Loading...
            </div>
          ) : isError ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-muted-foreground">
              <p className="text-sm text-destructive">
                {error instanceof Error ? error.message : "Failed to load jobs"}
              </p>
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                <RefreshCw className="mr-2 h-3 w-3" />
                Retry
              </Button>
            </div>
          ) : data?.data.length === 0 ? (
            <div className="flex h-full items-center justify-center p-8 text-muted-foreground">
              No jobs found
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Batches</TableHead>
                  <TableHead>Created By</TableHead>
                  <TableHead>Created At</TableHead>
                  <TableHead className="w-[1%]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.data.map((job) => (
                  <TableRow
                    key={job.id}
                    className="cursor-pointer hover:bg-muted/50"
                    tabIndex={0}
                    role="button"
                    aria-label={`View details for ${jobTypeLabel(job.type)} job`}
                    onClick={() => onSelectJob(job.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onSelectJob(job.id);
                      }
                    }}
                  >
                    <TableCell className="font-medium">
                      {jobTypeLabel(job.type)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={statusBadgeVariant(job.status)}>
                        {job.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {job.totalBatches}
                    </TableCell>
                    <TableCell className="text-sm">
                      {job.createdByUser?.name ??
                        job.createdByUser?.email ??
                        "—"}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(job.createdAt)}
                    </TableCell>
                    <TableCell>
                      <a
                        href={datadogJobUrl(job.id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ExternalLink className="h-3 w-3" />
                        Events
                      </a>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {data && data.meta.totalPages > 1 && (
          <div className="p-4 border-t flex items-center justify-between text-sm text-muted-foreground">
            <span>
              Page {data.meta.page} of {data.meta.totalPages} ({data.meta.total}{" "}
              total)
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= data.meta.totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

function JobDetailView({
  jobId,
  onBack,
}: {
  jobId: string;
  onBack: () => void;
}) {
  const [taskPage, setTaskPage] = useState(1);
  const [taskStatus, setTaskStatus] = useState<
    "pending" | "running" | "succeeded" | "failed" | undefined
  >(undefined);
  const [batchKey, setBatchKey] = useState<string | undefined>(undefined);
  const queryClient = useQueryClient();

  const { data, isLoading, isError, error, refetch } = useQuery(
    trpc.admin.getJobDetail.queryOptions(
      { jobId, page: taskPage, limit: 50, taskStatus, batchKey },
      {
        placeholderData: (prev) => prev,
        refetchInterval: (query) => {
          const job = query.state.data?.job;
          if (job?.status === "running" || job?.status === "pending") {
            return 3000;
          }
          return false;
        },
      },
    ),
  );

  const { mutate: cancelJob, isPending: isCancelling } = useMutation({
    mutationFn: () => trpcClient.admin.cancelJob.mutate({ jobId }),
    onSuccess: () => {
      toast.success("Job cancelled");
      queryClient.invalidateQueries({
        queryKey: trpc.admin.getJobDetail.queryKey(),
      });
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });

  const job = data?.job;

  return (
    <div className="flex flex-col gap-6 p-6 max-md:p-0 h-full">
      <PageHeader>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <span className="font-semibold">Job Detail</span>
        </div>
      </PageHeader>

      <Card className="flex-1 flex flex-col overflow-hidden max-md:border-0 max-md:shadow-none max-md:bg-transparent max-md:rounded-none">
        {isError ? (
          <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-muted-foreground">
            <p className="text-sm text-destructive">
              {error instanceof Error
                ? error.message
                : "Failed to load job details"}
            </p>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="mr-2 h-3 w-3" />
              Retry
            </Button>
          </div>
        ) : isLoading || !job ? (
          <div className="flex h-full items-center justify-center p-8 text-muted-foreground">
            Loading...
          </div>
        ) : (
          <>
            {/* Job summary header */}
            <div className="p-4 border-b bg-card text-card-foreground space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="font-medium">{jobTypeLabel(job.type)}</span>
                  <Badge variant={statusBadgeVariant(job.status)}>
                    {job.status}
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  <a
                    href={datadogJobUrl(jobId)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    View in Datadog
                  </a>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      queryClient.invalidateQueries({
                        queryKey: trpc.admin.getJobDetail.queryKey(),
                      })
                    }
                  >
                    <RefreshCw className="mr-2 h-3 w-3" />
                    Refresh
                  </Button>
                  {(job.status === "running" || job.status === "pending") && (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => {
                        if (
                          confirm(
                            "Cancel this job? In-flight batches may still complete.",
                          )
                        ) {
                          cancelJob();
                        }
                      }}
                      disabled={isCancelling}
                    >
                      <Ban className="mr-2 h-3 w-3" />
                      Cancel
                    </Button>
                  )}
                </div>
              </div>
              <div className="flex gap-6 text-sm text-muted-foreground">
                <span>Batches: {job.totalBatches}</span>
              </div>
              <div className="flex gap-4 text-xs text-muted-foreground">
                <span>
                  Created: {formatDate(job.createdAt)} by{" "}
                  {job.createdByUser?.name ?? job.createdByUser?.email}
                </span>
                {job.completedAt && (
                  <span>Completed: {formatDate(job.completedAt)}</span>
                )}
              </div>
            </div>

            {/* Batch filters */}
            <div className="p-3 border-b flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Status:</span>
                {(
                  [
                    undefined,
                    "succeeded",
                    "failed",
                    "pending",
                    "running",
                  ] as const
                ).map((s) => (
                  <Button
                    key={s ?? "all"}
                    variant={taskStatus === s ? "default" : "outline"}
                    size="sm"
                    onClick={() => {
                      setTaskStatus(s);
                      setTaskPage(1);
                    }}
                  >
                    {s ?? "All"}
                  </Button>
                ))}
              </div>
              {data.batchKeys.length > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Batch:</span>
                  <Select
                    value={batchKey ?? "__all__"}
                    onValueChange={(v) => {
                      setBatchKey(v === "__all__" ? undefined : v);
                      setTaskPage(1);
                    }}
                  >
                    <SelectTrigger className="h-8 w-[160px] text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">All batches</SelectItem>
                      {data.batchKeys.map((key) => (
                        <SelectItem key={key} value={key}>
                          {key}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Batches table */}
            <div className="flex-1 overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Batch</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Entities</TableHead>
                    <TableHead>Entity Type</TableHead>
                    <TableHead className="w-[1%]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.tasks.data.map((task) => (
                    <TableRow key={task.id}>
                      <TableCell className="font-mono text-xs">
                        {task.batchKey}
                      </TableCell>
                      <TableCell>
                        <Badge variant={statusBadgeVariant(task.status)}>
                          {task.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {task.totalEntities}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {task.entityType}
                      </TableCell>
                      <TableCell>
                        <a
                          href={datadogRunUrl(jobId, task.id)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                        >
                          <ExternalLink className="h-3 w-3" />
                          Traces
                        </a>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {data.tasks.meta.totalPages > 1 && (
              <div className="p-4 border-t flex items-center justify-between text-sm text-muted-foreground">
                <span>
                  Page {data.tasks.meta.page} of {data.tasks.meta.totalPages} (
                  {data.tasks.meta.total} batches)
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={taskPage <= 1}
                    onClick={() => setTaskPage(taskPage - 1)}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={taskPage >= data.tasks.meta.totalPages}
                    onClick={() => setTaskPage(taskPage + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
